import React, { useState, useEffect } from 'react';
import { ragAPI } from '../services/api';
import toast from 'react-hot-toast';
import ConfirmDeleteDialog from '../components/common/ConfirmDeleteDialog';

const RAGManagementPage = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [documents, setDocuments] = useState({ total: 0, sources: [], documents: [] });
  const [collections, setCollections] = useState([]);
  const [deleteDocDialogOpen, setDeleteDocDialogOpen] = useState(false);
  const [docNameToDelete, setDocNameToDelete] = useState(null);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, docsRes, collectionsRes] = await Promise.all([
        ragAPI.getStats(),
        ragAPI.getDocuments(),
        ragAPI.getCollections()
      ]);

      console.log('📊 API Responses:', {
        stats: statsRes.data,
        docs: docsRes.data,
        collections: collectionsRes.data
      });

      setStats(statsRes.data);
      setDocuments(docsRes.data || { total: 0, sources: [], documents: [] });
      setCollections(collectionsRes.data.collections || []);
      
      toast.success('Data loaded successfully');
    } catch (error) {
      console.error('Failed to load RAG data', error);
      toast.error('Failed to load RAG data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async () => {
    const docName = docNameToDelete;
    if (!docName) return;
    setDeleteDocDialogOpen(false);

    try {
      // Find the collection containing this document (default to 1)
      const collectionId = 1;
      await ragAPI.deleteDocument(collectionId, docName);
      toast.success(`Deleted ${docName}`);
      loadData();
    } catch (error) {
      console.error('Failed to delete document', error);
      toast.error('Failed to delete document');
    }
  };

  const handleClearDatabase = async () => {
    setClearAllDialogOpen(false);

    try {
      if (ragAPI.clearDatabase) {
        const { data } = await ragAPI.clearDatabase();
        
        if (data.success) {
          toast.success(`Successfully deleted ${data.deleted_count} documents`);
          loadData();
        } else {
          toast.error(data.message || 'Failed to clear database');
        }
      } else {
        toast.error('Clear database functionality not implemented');
      }
    } catch (error) {
      console.error('Failed to clear database', error);
      toast.error('Failed to clear database');
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading RAG data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">RAG Data Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your vector database and document sources</p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Refresh
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Collections</p>
              <p className="text-2xl font-semibold text-gray-900">{stats?.total_collections || 0}</p>
            </div>
            <div className="text-3xl">📚</div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Chunks</p>
              <p className="text-2xl font-semibold text-gray-900">{stats?.total_chunks || documents.total_chunks || 0}</p>
            </div>
            <div className="text-3xl">🧩</div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Unique Sources</p>
              <p className="text-2xl font-semibold text-gray-900">{documents.total || documents.sources?.length || 0}</p>
            </div>
            <div className="text-3xl">📁</div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Vector Store</p>
              <p className="text-lg font-semibold text-gray-900">
                {stats?.vector_store || stats?.environment || 'N/A'}
              </p>
            </div>
            <div className="text-3xl">🗄️</div>
          </div>
        </div>
      </div>

      {/* Collections */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Collections ({collections.length})
          </h2>
        </div>
        <div className="p-5">
          {collections && collections.length > 0 ? (
            <div className="space-y-3">
              {collections.map((collection, index) => (
                <div key={collection.id || index} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-gray-900">{collection.name || collection.collection_name}</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {collection.vector_store} | {collection.documents_count || 0} docs | {collection.chunks_count || 0} chunks
                      </p>
                    </div>
                    <div className="text-2xl">📦</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No collections found</p>
            </div>
          )}
        </div>
      </div>

      {/* Document Sources */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Document Sources ({documents.documents?.length || documents.sources?.length || 0})
          </h2>
        </div>
        <div className="p-5">
          {(documents.documents?.length > 0 || documents.sources?.length > 0) ? (
            <div className="space-y-2">
              {/* Use documents array if available, else fall back to sources */}
              {(documents.documents || documents.sources?.map((s, i) => ({ id: i, name: s, source: s, chunks_count: 0 }))).map((doc, index) => (
                <div key={doc.id || index} className="flex items-center justify-between p-3 border border-gray-100 rounded-md hover:bg-gray-50">
                  <div className="flex items-center flex-1">
                    <span className="text-2xl mr-3">
                      {(doc.name || doc.source || '').endsWith('.pdf') ? '📄' : 
                       (doc.name || doc.source || '').startsWith('http') ? '🌐' : '📝'}
                    </span>
                    <div>
                      <span className="text-sm text-gray-900 break-all">{doc.name || doc.source}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        ({doc.chunks_count || 0} chunks)
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => { setDocNameToDelete(doc.name || doc.source); setDeleteDocDialogOpen(true); }}
                    className="ml-2 p-2 text-red-500 hover:bg-red-50 rounded"
                    title="Delete document"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No documents in database</p>
              <p className="text-sm text-gray-400 mt-1">Upload documents in the Chat Serviti page to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-lg border border-red-200 shadow-sm">
        <div className="p-5 border-b border-red-200 bg-red-50">
          <h2 className="text-lg font-medium text-red-900">Danger Zone</h2>
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Clear All Vector Data</h3>
              <p className="text-sm text-gray-500 mt-1">
                Permanently delete all documents and embeddings from the vector database
              </p>
            </div>
            <button
              onClick={() => setClearAllDialogOpen(true)}
              disabled={!stats?.total_chunks && !documents.total_chunks}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Delete All Data
            </button>
          </div>
        </div>
      </div>
      {/* Delete Document Dialog */}
      <ConfirmDeleteDialog
        open={deleteDocDialogOpen}
        onClose={() => { setDeleteDocDialogOpen(false); setDocNameToDelete(null); }}
        onConfirm={handleDeleteDocument}
        title="Delete Document"
        entityName={docNameToDelete || ''}
        warningText="This will permanently remove this document and all its vector embeddings."
        confirmButtonText="Delete Document"
      />

      {/* Clear All Dialog */}
      <ConfirmDeleteDialog
        open={clearAllDialogOpen}
        onClose={() => setClearAllDialogOpen(false)}
        onConfirm={handleClearDatabase}
        title="Clear All Vector Data"
        entityName="DELETE ALL"
        warningText="This will permanently delete ALL documents and embeddings from the vector database. This action cannot be undone."
        confirmButtonText="Delete Everything"
      />
    </div>
  );
};

export default RAGManagementPage;